/**
 * Helper for emitting medical PA form analytics events to BigQuery with the
 * shared patient / order / user payload derived from the order details.
 *
 * @module medicalPaForm/components/utils/formEventLogger
 */
import { User } from "../../../../data-model/firebaseAuthModel";
import { EventsName } from "../../../../enums/eventsName";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { GeneralOrderDetailsResponse } from "../../types";

/** Inputs for {@link enqueueFormEvent}. */
export interface EnqueueFormEventParams {
  /** Order details used to derive patient and order identifiers. */
  generalOrderDetails: GeneralOrderDetailsResponse | null;
  /** Organisation ID scoping the event. */
  orgId: string | null;
  /** Authenticated user (for user id/email attribution). */
  user: User | null;
  /** BigQuery event name. */
  eventName: EventsName;
  /** Event-specific fields merged into `additional_data`. */
  additionalData: Record<string, unknown>;
}

/**
 * Builds the common analytics payload (patient/order/org/user) and enqueues the
 * event via {@link apiWorkerManager}.
 *
 * @param params - See {@link EnqueueFormEventParams}.
 * @returns The promise from the worker enqueue call (never rejects internally).
 */
export const enqueueFormEvent = ({
  generalOrderDetails,
  orgId,
  user,
  eventName,
  additionalData,
}: EnqueueFormEventParams): Promise<unknown> =>
  apiWorkerManager.enqueueAndProcess({
    patient_id:
      generalOrderDetails?.order?.demographics?.patient_id ??
      generalOrderDetails?.order?.demographics?.patient_mrn ??
      "",
    order_id: generalOrderDetails?.order?.order_id ?? "",
    org_id: orgId ?? "",
    event_name: eventName,
    user_id: user?.id ?? "",
    user_email: user?.email ?? "",
    additional_data: additionalData,
  });
